import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ArrowRight, Lock } from "lucide-react";

export default function Login() {
    const { login } = useAuth();
    const nav = useNavigate();
    const [code, setCode] = useState("");
    const [busy, setBusy] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        const c = code.trim();
        if (!c) return toast.error("Enter your access code");
        if (!/^\d+$/.test(c)) return toast.error("Access code must be numeric");
        setBusy(true);
        try {
            const u = await login(c);
            toast.success(`Welcome, ${u.name || "back"}`);
            nav(u.role === "admin" ? "/wishlist" : "/shop");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Invalid access code");
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="min-h-screen flex bg-white">
            {/* Left panel: hero */}
            <div className="hidden md:flex md:w-1/2 relative overflow-hidden border-r border-border">
                <img
                    src="https://images.unsplash.com/photo-1593250816874-8edf4f732edb?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTF8MHwxfHNlYXJjaHwxfHx0ZXh0aWxlJTIwZmFicmljJTIwdGV4dHVyZSUyMGx1eHVyeSUyMG1hY3JvfGVufDB8fHx8MTc4NTcwNDYxNnww&ixlib=rb-4.1.0&q=85"
                    alt="Textile"
                    className="absolute inset-0 w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-white/30" />
                <div className="relative z-10 p-12 flex flex-col justify-between w-full">
                    <div>
                        <div className="overline text-black/70">Wholesale · Textile Manufacturing</div>
                        <h1 className="font-display text-5xl md:text-6xl tracking-tight leading-none mt-3 text-black whitespace-nowrap">
                            A V Corp<span className="text-yellow-400">.</span>
                        </h1>
                    </div>
                    <div className="max-w-md">
                        <p className="font-body text-sm text-black/70 leading-relaxed">
                            Ladies wear at wholesale — new arrivals &amp; best sellers curated for our partner buyers, ready for bulk order.
                        </p>
                        <div className="mt-6 grid grid-cols-4 gap-x-1 text-[10px] tracking-widest text-black/60 uppercase">
                            <div className="border-t border-black/60 pt-2">Dresses</div>
                            <div className="border-t border-black/60 pt-2">Cord Sets</div>
                            <div className="border-t border-black/60 pt-2">Trousers</div>
                            <div className="border-t border-black/60 pt-2">Night Wear</div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Right panel: form */}
            <div className="flex-1 flex items-center justify-center p-8">
                <div className="w-full max-w-md">
                    <div className="mb-10">
                        <div className="overline text-neutral-500 mb-3">Sign in</div>
                        <h2 className="font-display text-4xl tracking-tight leading-none">Enter your access code</h2>
                        <p className="font-body text-sm text-neutral-500 mt-3 leading-relaxed">
                            One numeric code per user. No usernames. Contact A V Corp if you need one.
                        </p>
                    </div>

                    <form onSubmit={submit} className="space-y-5" data-testid="login-form">
                        <div>
                            <Label htmlFor="code" className="overline text-neutral-600">Access Code</Label>
                            <Input
                                id="code"
                                data-testid="login-code-input"
                                value={code}
                                onChange={(e) => setCode(e.target.value.replace(/[^0-9]/g, ""))}
                                placeholder="Numeric code"
                                inputMode="numeric"
                                pattern="[0-9]*"
                                type="password"
                                autoComplete="one-time-code"
                                className="mt-2 rounded-none h-12 border-black focus-visible:ring-1 focus-visible:ring-black tabular-nums tracking-widest text-lg"
                                autoFocus
                            />
                        </div>
                        <Button
                            type="submit"
                            data-testid="login-submit-btn"
                            disabled={busy}
                            className="w-full h-12 rounded-none bg-black text-white hover:bg-neutral-800 uppercase tracking-widest text-xs font-semibold group"
                        >
                            {busy ? "Signing in…" : "Sign in"}
                            <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" strokeWidth={1.5}/>
                        </Button>
                    </form>

                    <div className="mt-10 border-t border-border pt-6 text-xs font-body text-neutral-500 leading-relaxed">
                        <div className="flex items-center gap-2 text-neutral-700">
                            <Lock className="h-3 w-3" strokeWidth={1.5} />
                            <span className="tracking-widest uppercase">Secure Access</span>
                        </div>
                        <p className="mt-2 text-neutral-500">
                            Access is restricted to A V Corp staff and approved clients. Contact the owner if you need a code.
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
}
